import { useState } from "react";
import { FaFilePdf, FaDownload } from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import ReportPdfPreview, {
  downloadPdfFromBase64,
} from "../../../Components/Reports/ReportPdfPreview";
import ReportVersionFilter, {
  createVersionCriterion,
} from "../../../Components/Reports/ReportVersionFilter";
import useReportFirms from "../../../Hooks/useReportFirms";
import {
  useLazyGetDeviceGeneralInfoReportQuery,
  useGetDeviceGeneralInfoPdfReportMutation,
} from "../../../store/storageRoles/reportsApi";
import {
  FirmDropdown,
  TextInput,
  CustomDropdown,
} from "../../../Components/atoms";
import {
  describeVersionCriterion,
  isVersionCriterionReady,
  matchesVersionCriteria,
} from "../../../utils/versionUtils";

/**
 * CİHAZ GENEL BİLGİ RAPORU — Excel/tablo yerine PDF belgesi üretir.
 *
 * Desen `FirmPerformanceUnifiedReportPage` ile aynıdır: `ReportLayout`
 * `showExcel={false}` + başlıkta "PDF İndir" + solda `ReportFilterCard`
 * + sağda `ReportPdfPreview`.
 *
 * AKIŞ İKİ ADIMLIDIR ama TEK BUTONLA yürür:
 *   1) `generalInfo` uç noktasından ham cihaz listesi çekilir (veri kaynağı
 *      DEĞİŞMEDİ),
 *   2) sürüm kriterleri BURADA uygulanır (`matchesVersionCriteria`),
 *   3) süzülmüş satırlar PDF uç noktasına gönderilir ve belge basılır.
 *
 * SÜRÜM FİLTRESİ NEDEN SUNUCUYA TAŞINMADI: kriterler `utils/versionUtils.js`
 * içinde istemcide yorumlanıyor. Aynı mantığı sunucuya kopyalasaydık iki ayrı
 * süzme uygulaması doğar ve zamanla ayrışırdı — belge ile ekranda görülen
 * liste aynı filtreyi farklı yorumlar hâle gelirdi. Bunun yerine panel,
 * süzdüğü satırların AYNISINI gönderir; sunucu yalnızca belgeyi basar.
 */
export default function ReportDeviceGeneralInfo() {
  const { auth } = useAuth();
  const token = auth.accessToken;

  const [serialNo, setSerialNo] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [firmId, setFirmId] = useState("");
  const [firmName, setFirmName] = useState("");
  const [deviceStatusName, setDeviceStatusName] = useState("");
  // Sürüm kriterleri (VE ile birleşir) — bkz. utils/versionUtils.js
  const [versionCriteria, setVersionCriteria] = useState([
    createVersionCriterion(),
  ]);

  // Rapor çalıştırıldıktan sonraki durum. `deviceCount === 0` ile
  // `deviceCount === null` (hiç çalıştırılmadı) ayrı durumlardır: ilki
  // "kriterlere uyan cihaz yok", ikincisi "rapor bekleniyor".
  const [deviceCount, setDeviceCount] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  const { firms, isLoading: firmDataIsLoading } = useReportFirms(token);

  const [getDeviceGeneralInfoReport] = useLazyGetDeviceGeneralInfoReportQuery();
  const [generatePdf, pdfState] = useGetDeviceGeneralInfoPdfReportMutation();

  const report = pdfState.data?.data;
  const pdfBase64 = report?.pdfBase64;

  const deviceStatusOptions = [
    { id: "all", label: "Tüm Durumlar", value: "" },
    { id: "servis", label: "Servis", value: "Servis" },
    { id: "depo", label: "Depo", value: "Depo" },
    { id: "firma", label: "Firma", value: "Firma" },
    { id: "plasiyer", label: "Plasiyer", value: "Plasiyer" },
  ];

  // Sadece eksiksiz kriterler uygulanır (yarım doldurulan satır yok sayılır)
  const activeVersionCriteria = versionCriteria.filter(isVersionCriterionReady);

  // Seçili filtre chip'leri — aynı liste belgenin üst kısmına da yazılır ki
  // eldeki PDF'in hangi filtreyle üretildiği belgeden okunabilsin.
  const activeFilterChips = [
    { label: "Seri No", value: serialNo },
    { label: "Cihaz Adı", value: deviceName },
    { label: "Firma", value: firmId ? firmName?.firmName : "" },
    { label: "Durum", value: deviceStatusName },
    ...activeVersionCriteria.map((criterion) => ({
      label: "Sürüm",
      value: describeVersionCriterion(criterion),
    })),
  ];

  const filterLines = activeFilterChips
    .filter((chip) => chip.value)
    .map((chip) => `${chip.label}: ${chip.value}`);

  const handleFirmChange = (firm) => {
    if (!firm || firm._id === "all") {
      setFirmId("");
      setFirmName({ _id: "all", firmName: "Tüm Firmalar" });
    } else {
      setFirmId(firm._id);
      setFirmName(firm);
    }
  };

  const handleSearch = async () => {
    setRunError(null);
    setIsRunning(true);
    try {
      // 1) Ham veri — uç nokta ve parametreler DEĞİŞMEDİ.
      const response = await getDeviceGeneralInfoReport({
        serialNo,
        deviceName,
        firmId,
        deviceStatusName,
        token,
      }).unwrap();

      // 2) Sürüm kriterleri istemcide uygulanır (tek kaynak: versionUtils).
      const filtered = (response?.data || []).filter((item) =>
        matchesVersionCriteria(item, activeVersionCriteria)
      );
      setDeviceCount(filtered.length);

      // Boş sonuçta PDF üretmeye gerek yok: boş belge basmak hem sunucuda
      // gereksiz bir puppeteer turu hem de kullanıcı için anlamsız bir çıktı.
      if (filtered.length === 0) {
        pdfState.reset?.();
        return;
      }

      // 3) Süzülmüş satırlar belgeye basılır.
      await generatePdf({ token, rows: filtered, filterLines }).unwrap();
    } catch (error) {
      setRunError(error);
    } finally {
      setIsRunning(false);
    }
  };

  const handleClearFilters = () => {
    setSerialNo("");
    setDeviceName("");
    setFirmId("");
    setFirmName("");
    setDeviceStatusName("");
    setVersionCriteria([createVersionCriterion()]);
    setDeviceCount(null);
    setRunError(null);
    pdfState.reset?.();
  };

  const errorMessage =
    runError?.data?.message ||
    runError?.error ||
    runError?.message ||
    "Rapor oluşturulurken bir hata oluştu.";

  const headerActions = pdfBase64 ? (
    <button
      onClick={() => downloadPdfFromBase64(pdfBase64, report?.fileName)}
      title="PDF İndir"
      className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors text-xs font-SemiBold"
    >
      <FaFilePdf className="w-4 h-4" />
      <FaDownload className="w-3 h-3" />
      <span className="hidden sm:inline">PDF İndir</span>
    </button>
  ) : null;

  return (
    <ReportLayout
      title="Cihaz Genel Bilgi Raporu"
      showExcel={false}
      headerActions={headerActions}
      sidebar={
        <ReportFilterCard
          title="Cihaz Genel Bilgi Filtreleme"
          description="Rapor için filtre seçeneklerini belirleyin"
          showDatePickers={false}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={isRunning}
          searchButtonText="Raporu Getir"
          clearButtonText="Temizle"
          /* Sürüm filtresi artık tabloyu anlık süzmüyor; belgeye girdiği için
             değişiklikten sonra raporun yeniden üretilmesi gerekiyor. */
          searchDisabledMessage="Sürüm filtresini değiştirdikten sonra belgeyi yenilemek için 'Raporu Getir'e basın"
          activeFilters={activeFilterChips}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Seri No */}
            <TextInput
              label="Seri No"
              placeholder="Seri numarası girin..."
              value={serialNo}
              onChange={(e) => setSerialNo(e.target.value)}
            />

            {/* Cihaz Adı */}
            <TextInput
              label="Cihaz Adı"
              placeholder="Cihaz adı girin..."
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
            />

            {/* Firma */}
            <FirmDropdown
              value={firmName}
              onChange={handleFirmChange}
              firms={firms}
              loading={firmDataIsLoading}
            />

            {/* Cihaz Durumu */}
            <CustomDropdown
              value={deviceStatusOptions.find(
                (opt) => opt.value === deviceStatusName
              )}
              onChange={(option) => setDeviceStatusName(option?.value || "")}
              options={deviceStatusOptions}
              placeholder="Cihaz durumu seçin..."
              showSearch={false}
            />
          </div>
        </ReportFilterCard>
      }
    >
      {/* SÜRÜM FİLTRESİ NEDEN KENAR ÇUBUĞUNDA DEĞİL:
          `ReportVersionFilter` bir satırı dört sütuna açıyor
          (`sm:grid-cols-[1.2fr_1fr_1fr_auto]`) ve Tailwind kırılımları görünüm
          penceresine bakıyor — 360px'lik kenar çubuğunda da dört sütuna açılıp
          her açılır listeyi ~75px'e sıkıştırıyordu. Panel olduğu gibi KORUNDU
          (dosyaya dokunulmadı), yalnızca geniş sütuna, belgenin hemen üstüne
          alındı. Süzme sonucu doğrudan PDF'e gider. */}
      <div className="shrink-0 bg-white rounded-xl border border-gray-100 px-3 sm:px-4 pt-1 pb-3">
        <ReportVersionFilter
          criteria={versionCriteria}
          onChange={setVersionCriteria}
        />
        <p className="mt-2 text-[11px] text-gray-400">
          Sürüm filtresi belgeye işlenir; değişiklikten sonra “Raporu Getir” ile
          belgeyi yenileyin.
        </p>
      </div>

      {isRunning && (
        <ReportLoadingState
          title="Cihaz Genel Bilgi Raporu Hazırlanıyor"
          description="Cihazlar süzülüyor ve belge oluşturuluyor, lütfen bekleyin..."
          progressText="Veriler işleniyor..."
          skeletonRows={6}
          skeletonColumns={4}
        />
      )}

      {!isRunning && runError && (
        <ReportErrorState
          title="Hata Oluştu"
          message={errorMessage}
          icon="warning"
        />
      )}

      {!isRunning && !runError && pdfBase64 && (
        <ReportPdfPreview
          pdfBase64={pdfBase64}
          title={`Cihaz Genel Bilgi Raporu — ${deviceCount ?? 0} cihaz`}
        />
      )}

      {!isRunning && !runError && !pdfBase64 && (
        <ReportEmptyState
          title={deviceCount === 0 ? "Veri Bulunamadı" : "Rapor Bekleniyor"}
          message={
            deviceCount === 0
              ? "Seçilen kriterlere (sürüm filtreleri dâhil) uygun cihaz bulunamadı."
              : "Filtreleri belirleyip 'Raporu Getir' butonuna basın."
          }
          icon="document"
        />
      )}
    </ReportLayout>
  );
}
